import { z } from "zod";
import { DocumentSettingsSchema, DocumentSettingsSearchSchema } from "./document-settings";
import { AccountSchema, AccountSearchSchema } from "./account";
import { customErrorMap, myPreProcess } from "../shared/validation/zod";
import { DocumentItemAddSchema, DocumentItemEditSchema, DocumentItemSchema } from "./document-item";
import { DocumentWorkAddSchema, DocumentWorkEditSchema, DocumentWorkSchema } from "./document-work";
import { CarSearchSchema } from "./car";
import dayjs from "dayjs";
import { DocumentCarAddSchema, DocumentCarEditSchema, DocumentCarSchema } from "./document-car";
import { DocumentAccountAddSchema, DocumentAccountEditSchema, DocumentAccountSchema } from "./document-account";
import { DocumentPaymentFormSchema, DocumentPaymentSchema } from "./document-payment";
import { EntrySchema } from "./entry";
import { DocumentClosingFormSchema, DocumentClosingSchema } from "./document-closing";
import { DocumentDepositAddSchema, DocumentDepositSchema } from "./document-deposit";
import { documentStatusSchema } from "./types";

z.setErrorMap(customErrorMap);

// const OptionalCarSchema = CarSchema.nullable();


export const DocumentSchema = z.object({

    // required
    id: z.number(),
    business_id: z.number(),
    document_settings_id: z.number(),
    account_id: z.number(),
    document_number: z.number(),
    date_time: z.preprocess((val) => myPreProcess(val, dayjs(val as string).format('YYYY-MM-DD HH:mm')), z.string()),
    status: documentStatusSchema,
    

    created_at: z.string(),
    updated_at: z.string(),

    // nullable
    total: z.coerce.number().nullable(),
    include_vat: z.coerce.number().nullable(),
    not_include_vat: z.coerce.number().nullable(),
    vat: z.coerce.number().nullable(),
    to_pay: z.coerce.number().nullable(),
    balance: z.coerce.number().nullable(),
    discount: z.coerce.number().nullable(),
    discount_percentage: z.coerce.number().nullable(),
    reference_id: z.number().nullable(),
    works_total: z.coerce.number().nullable(),
    items_total: z.coerce.number().nullable(),
    reference: z.string().nullable(),
    receipt: z.coerce.number().nullable(),
    deposit: z.coerce.number().nullable(),


    // loads
    document_settings: DocumentSettingsSchema.nullish(),
    account: AccountSchema.nullish(),
    document_account: DocumentAccountSchema.nullish(),
    car: DocumentCarSchema.nullish(),
    items: z.array(DocumentItemSchema).nullish(),
    works: z.array(DocumentWorkSchema).nullish(),
    payments: z.array(DocumentPaymentSchema).nullish(),
    entrys: z.array(EntrySchema).nullish(),
    documents_closing: z.array(DocumentClosingSchema).nullish(),
    deposits: z.array(DocumentDepositSchema).nullish(),
    closing: z.array(DocumentClosingSchema).nullish(),

})


export const DocumentEditSchema = z.object({

    // required
    id: z.number(),
    account_id: z.number(),
    document_number: z.number(),
    status: documentStatusSchema,
    

    // optional and nullable
    reference_id: z.number().nullish(),
    works_total: z.coerce.number().nullish(),
    items_total: z.coerce.number().nullish(),
    reference: z.string().nullish(),
    receipt: z.coerce.number().nullish(),
    deposit: z.coerce.number().nullish(),

    total: z.coerce.number().gte(0).nullish(),
    include_vat: z.coerce.number().nullish(),
    not_include_vat: z.coerce.number().nullish(),
    vat: z.coerce.number().nullish(),
    to_pay: z.coerce.number().gte(0).nullish(),
    balance: z.coerce.number().nullish(),
    discount: z.coerce.number().nullish(),
    discount_percentage: z.coerce.number().nullish(),

    // required fields
    document_settings_id: z.preprocess((val) => myPreProcess(val, Number(val)), z.number()),
    date_time: z.preprocess((val) => myPreProcess(val, dayjs(val as string).format('YYYY-MM-DD HH:mm')), z.string()),


    //loads
    document_settings: DocumentSettingsSchema.nullish(),
    document_account: DocumentAccountEditSchema.nullish(),
    car: DocumentCarEditSchema.nullish(),
    items: z.array(DocumentItemEditSchema).nullish(),
    works: z.array(DocumentWorkEditSchema).nullish(),
    payments: z.array(DocumentPaymentFormSchema).nullish(),
    documents_closing: z.array(DocumentClosingFormSchema).nullish(),
    deposits: z.array(DocumentDepositAddSchema).nullish(),

})

export const DocumentAddSchema = z.object({

    // required
    account_id: z.number(),
    document_number: z.number(),
    status: documentStatusSchema,
    

    // optional and nullable
    total: z.coerce.number().gte(0).nullish(),
    include_vat: z.coerce.number().nullish(),
    not_include_vat: z.coerce.number().nullish(),
    vat: z.coerce.number().nullish(),
    to_pay: z.coerce.number().gte(0).nullish(),
    balance: z.coerce.number().nullish(),
    reference_id: z.number().nullish(),
    works_total: z.coerce.number().nullish(),
    items_total: z.coerce.number().nullish(),
    reference: z.string().nullish(),
    receipt: z.coerce.number().nullish(),
    deposit: z.coerce.number().nullish(),
    discount: z.coerce.number().nullish(),
    discount_percentage: z.coerce.number().nullish(),

    // required fields
    document_settings_id: z.preprocess((val) => myPreProcess(val, Number(val)), z.number()),
    date_time: z.preprocess((val) => myPreProcess(val, dayjs(val as string).format('YYYY-MM-DD HH:mm')), z.string()),


    //loads
    document_settings: DocumentSettingsSchema.nullish(),
    document_account: DocumentAccountAddSchema.nullish(),
    car: DocumentCarAddSchema.nullish(),
    items: z.array(DocumentItemAddSchema).nullish(),
    works: z.array(DocumentWorkAddSchema).nullish(),
    payments: z.array(DocumentPaymentFormSchema).nullish(),
    documents_closing: z.array(DocumentClosingFormSchema).nullish(),
    deposits: z.array(DocumentDepositAddSchema).nullish(),

})


export const DocumentSearchSchema = z.object({


    account_id: z.preprocess((val) => myPreProcess(val, Number(val)), z.number().nullish()),
    document_number: z.preprocess((val) => myPreProcess(val, Number(val)), z.number().nullish()),
    date_time: z.preprocess((val) => myPreProcess(val, dayjs(val as string).format('YYYY-MM-DD')), z.string().nullish()),
    status: z.preprocess((val) => myPreProcess(val), z.string().nullish()),
    total: z.preprocess((val) => myPreProcess(val, Number(val)), z.coerce.number().nullish()),
    vat: z.preprocess((val) => myPreProcess(val, Number(val)), z.coerce.number().nullish()),
    to_pay: z.preprocess((val) => myPreProcess(val, Number(val)), z.coerce.number().nullish()),
    balance: z.preprocess((val) => myPreProcess(val, Number(val)), z.coerce.number().nullish()),
    works_total: z.preprocess((val) => myPreProcess(val, Number(val)), z.coerce.number().nullish()),
    items_total: z.preprocess((val) => myPreProcess(val, Number(val)), z.coerce.number().nullish()),
    discount: z.preprocess((val) => myPreProcess(val, Number(val)), z.coerce.number().nullish()),
    reference: z.preprocess((val) => myPreProcess(val), z.string().nullish()),


    // loads
    document_settings: DocumentSettingsSearchSchema.optional(),
    account: AccountSearchSchema.optional(),
    document_account: AccountSearchSchema.optional(),
    car: CarSearchSchema.optional(),

});

export type Document = z.infer<typeof DocumentSchema>
export type DocumentEdit = z.infer<typeof DocumentEditSchema>
export type DocumentAdd = z.infer<typeof DocumentAddSchema>
export type DocumentSearch = z.infer<typeof DocumentSearchSchema>
